import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Loader2, CheckCircle2 } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

import { CONTACT, SERVICE_OPTIONS, IMG } from "../lib/site";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { AskExpertButton, LeaveReviewButton } from "../components/Buttons";
import { submitQuote } from "../lib/api";

const FIELD =
  "bg-bb-bg border-white/15 rounded-none text-bb-silver focus-visible:ring-1 focus-visible:ring-bb-gold focus-visible:border-bb-gold placeholder:text-bb-silver/30 h-12";
const LABEL =
  "text-xs uppercase tracking-widest text-bb-silver/70 mb-2 block font-cond";

const EMPTY = { name: "", company: "", phone: "", email: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please select the service you need.");
      return;
    }
    setLoading(true);
    try {
      await submitQuote(form);
      toast.success("Quote request received. We'll be in touch shortly.");
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-bb-bg">
      <PageHero
        eyebrow="[ Let's Talk ]"
        title="Contact"
        image={IMG.pumpSunset}
        alt="Pumpjack against a golden sunset"
        testid="contact-hero"
      />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT: details */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display uppercase text-3xl md:text-5xl text-bb-silver leading-[0.95] mb-8">
                Reach the crew
              </h2>
              <p className="text-bb-silver/70 leading-relaxed mb-10 max-w-md">
                Based in Hagerman and running the Delaware Basin daily. Call,
                email, or send the quote form; you'll get a straight answer from
                the owner.
              </p>
            </Reveal>

            <div className="space-y-8">
              {[
                { icon: MapPin, label: "Location", value: CONTACT.location },
                { icon: MapPin, label: "Service Area", value: CONTACT.serviceArea },
                { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone}` },
                { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
              ].map((row, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div
                    data-testid={`contact-detail-${i}`}
                    className="flex items-start gap-5 border-b border-white/10 pb-6"
                  >
                    <span className="shrink-0 h-11 w-11 border border-bb-gold/40 flex items-center justify-center">
                      <row.icon className="h-5 w-5 text-bb-gold" />
                    </span>
                    <div>
                      <p className="font-mono2 text-[11px] uppercase tracking-widest text-bb-silver/40 mb-1">
                        {row.label}
                      </p>
                      {row.href ? (
                        <a
                          href={row.href}
                          className="text-bb-silver text-lg hover:text-bb-gold transition-colors break-words"
                        >
                          {row.value}
                        </a>
                      ) : (
                        <p className="text-bb-silver text-lg">{row.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-3 mt-10">
                <AskExpertButton testid="contact-ask-expert-btn" />
                <LeaveReviewButton testid="contact-leave-review-btn" />
              </div>
            </Reveal>
          </div>

          {/* RIGHT: quote form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div
                id="quote-form"
                data-testid="quote-form"
                className="bg-bb-surface border border-white/10 p-6 md:p-10 scroll-mt-28"
              >
                <p className="text-bb-gold font-mono2 text-xs uppercase tracking-mega mb-3">
                  [ Request a Quote ]
                </p>
                <h3 className="font-display uppercase text-3xl md:text-4xl text-bb-silver mb-8">
                  Tell us what you need
                </h3>

                {done ? (
                  <div
                    data-testid="quote-success"
                    className="flex flex-col items-center text-center py-14"
                  >
                    <CheckCircle2 className="h-16 w-16 text-bb-gold mb-6" />
                    <h4 className="font-display uppercase text-2xl text-bb-silver mb-3">
                      Request received
                    </h4>
                    <p className="text-bb-silver/60 max-w-sm mb-8">
                      Thanks. We've got your details and will be in touch
                      shortly. Need something faster? Give us a call.
                    </p>
                    <button
                      type="button"
                      data-testid="quote-send-another-btn"
                      onClick={() => setDone(false)}
                      className="font-cond uppercase tracking-widest text-bb-gold hover:text-bb-goldhi transition-colors"
                    >
                      Send another request
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label className={LABEL}>Name *</Label>
                        <Input
                          required
                          data-testid="quote-name-input"
                          value={form.name}
                          onChange={upd("name")}
                          placeholder="Your name"
                          className={FIELD}
                        />
                      </div>
                      <div>
                        <Label className={LABEL}>Company</Label>
                        <Input
                          data-testid="quote-company-input"
                          value={form.company}
                          onChange={upd("company")}
                          placeholder="Company name"
                          className={FIELD}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label className={LABEL}>Phone *</Label>
                        <Input
                          required
                          data-testid="quote-phone-input"
                          value={form.phone}
                          onChange={upd("phone")}
                          placeholder="Your phone number"
                          className={FIELD}
                        />
                      </div>
                      <div>
                        <Label className={LABEL}>Email *</Label>
                        <Input
                          required
                          type="email"
                          data-testid="quote-email-input"
                          value={form.email}
                          onChange={upd("email")}
                          placeholder="you@company.com"
                          className={FIELD}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className={LABEL}>Service Needed *</Label>
                      <Select
                        value={form.service}
                        onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                      >
                        <SelectTrigger
                          data-testid="quote-service-select"
                          className={`${FIELD} data-[placeholder]:text-bb-silver/30`}
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-bb-surface border-white/15 rounded-none text-bb-silver">
                          {SERVICE_OPTIONS.map((opt) => (
                            <SelectItem
                              key={opt}
                              value={opt}
                              className="focus:bg-bb-gold/20 focus:text-bb-silver rounded-none"
                            >
                              {opt}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className={LABEL}>Message</Label>
                      <Textarea
                        data-testid="quote-message-input"
                        value={form.message}
                        onChange={upd("message")}
                        placeholder="Tell us about the job: location, timing, scope..."
                        rows={4}
                        className="bg-bb-bg border-white/15 rounded-none text-bb-silver focus-visible:ring-1 focus-visible:ring-bb-gold focus-visible:border-bb-gold placeholder:text-bb-silver/30"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="quote-submit-btn"
                      className="btn-sweep w-full inline-flex items-center justify-center gap-2 bg-bb-gold text-[#0a0a0a] font-cond font-bold uppercase tracking-widest py-4 hover:bg-bb-goldhi transition-colors disabled:opacity-60"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Sending
                        </>
                      ) : (
                        <span className="relative z-10">Request a Quote</span>
                      )}
                    </button>
                    <p className="text-bb-silver/30 text-xs font-mono2 text-center">
                      Submissions are stored securely. Swap in your email/endpoint anytime.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
